import React, { useState } from 'react';
import useDialog from '../hooks/useDialog';
import { useMonetization } from '../contexts/MonetizationContext';
import { useTranslation } from '../contexts/LanguageContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faLightbulb, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import './StreakRecoveryModal.css';

/**
 * StreakRecoveryModal - Shows when a player returns after missing days and their streak is lost/at risk.
 * Offers two recovery options: pay with hints or buy with money from the store.
 */
function StreakRecoveryModal({ recovery, onClose, onOpenStore }) {
    const { recoverStreakWithHints, hintBalance, purchasesEnabled, PURCHASE_OPTIONS } = useMonetization();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [recovered, setRecovered] = useState(false);
    const [error, setError] = useState(null);

    useBodyScrollLock(recovery && recovery.available);

    const dialogRef = useDialog(!!(recovery && recovery.available), () => onClose(false));

    if (!recovery || !recovery.available) return null;

    const canAffordHints = hintBalance >= recovery.costHints;

    const handleRecoverWithHints = async () => {
        setLoading(true);
        setError(null);
        const result = await recoverStreakWithHints();
        setLoading(false);

        if (result.success) {
            setRecovered(true);
            setTimeout(() => onClose(true), 1500);
        } else {
            setError(result.error || t('streak.recovery.failed'));
        }
    };

    const handleBuyFromStore = () => {
        onClose(false);
        if (onOpenStore) onOpenStore('streak_recovery');
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !loading) {
            onClose(false);
        }
    };

    return (
        <div className="modal-overlay-base recovery-overlay" onClick={handleOverlayClick}>
            <div className="modal-content-base recovery-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true">
                {!loading && (
                    <button
                        className="recovery-close"
                        onClick={() => onClose(false)}
                        aria-label={t('common.close')}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}

                <div className="recovery-header">
                    <div className="recovery-icon">
                        <span role="img" aria-label={t('streak.recovery.brokenLabel')}>&#x1F494;</span>
                    </div>
                    <h2>{t('streak.recovery.title')}</h2>
                    <p className="recovery-detail">
                        {t.rich('streak.recovery.broken', { b: <strong /> }, { count: recovery.lostStreak })}
                    </p>
                    <p className="recovery-missed">
                        {t('streak.recovery.missed', { count: recovery.daysMissed })}
                    </p>
                </div>

                {recovered ? (
                    <div className="recovery-success">
                        <div className="recovery-success-icon">
                            <span role="img" aria-label={t('streak.milestone.fireLabel')}>&#x1F525;</span>
                        </div>
                        <p>{t('streak.recovery.recovered')}</p>
                    </div>
                ) : (
                    <>
                        <div className="recovery-options">
                            <button
                                className={`recovery-hint-button ${!canAffordHints ? 'disabled' : ''}`}
                                onClick={handleRecoverWithHints}
                                disabled={loading || !canAffordHints}
                            >
                                {loading ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {t('streak.recovery.recovering')}</>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faLightbulb} />
                                        <span>{t('streak.recovery.withHints', { count: recovery.costHints })}</span>
                                    </>
                                )}
                            </button>
                            {!canAffordHints && (
                                <p className="recovery-insufficient">
                                    {t('streak.recovery.balance', { count: hintBalance })}
                                </p>
                            )}

                            {purchasesEnabled && (
                                <>
                                    <div className="recovery-divider">
                                        <span>{t('common.or')}</span>
                                    </div>

                                    <button
                                        className="recovery-buy-button"
                                        onClick={handleBuyFromStore}
                                        disabled={loading}
                                    >
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <span>{t('streak.recovery.buy', { price: PURCHASE_OPTIONS.streak_recovery.price })}</span>
                                    </button>
                                </>
                            )}
                        </div>

                        {error && (
                            <p className="recovery-error">{error}</p>
                        )}

                        {purchasesEnabled && (
                            <p className="recovery-dismiss">
                                {t('streak.recovery.later')}
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default StreakRecoveryModal;
